import { html, nothing, TemplateResult } from 'lit';
import { property } from 'lit/decorators.js';
import { MobxLitElement } from '@adobe/lit-mobx';
import { runInAction } from 'mobx';
import { comsStore } from '@mfe-4cs/state/ComsStore';
import { Service } from '@mfe-4cs/util/Service';
import { TefComs } from '@mfe-4cs/types/typings';
import { Tracker } from '@mfe-4cs/classes/Tracker';
import { TemplatingMixin } from '@mfec/lit/mixins/TemplatingMixin';
import { templates } from '@mfe-4cs/components/templates';
import './TefComsTriStateButton';
import './TefComsPagination';
import './tef-coms-customer-attribute';
import { ConsentGroupStore } from '@mfe-4cs/state/ConsentGroupStore';
import { DisplayMode, Generation } from '@api-consent/types/common';
import { Theme } from '@mfec/types/enum';

/**
 * The custom element `<tef-coms4cs>`.
 */
export class TefComs4csMicroFrontend extends TemplatingMixin(MobxLitElement, Theme.OCCL, templates) {
  @property({ type: String }) apiUri = '';
  @property({ type: String }) agentToken = '';
  @property({ type: String }) gatewayToken = '';
  @property({ type: String }) lifecycle = '';
  @property({ type: String }) mode = '';
  @property({ type: String }) traceid = '';
  @property({ type: String }) occlUri = '';
  @property({ type: String }) styleContext = '';
  @property({ type: String }) consentwhitelist = '';
  @property({ type: String }) channel = '';
  @property({ type: String }) usertype = '';
  @property({ type: String }) vonr = '';
  @property({ type: String }) references = '';
  @property({ type: Boolean }) showReferences = false;
  @property({ type: Boolean }) showVersions = false;
  @property({ type: Boolean }) isExistingRegister = false;
  @property({ type: Boolean }) isNewCustomer = false;
  @property({ type: String }) spId = '';
  @property({ type: String }) lcId = '';
  @property({ type: String }) registerId = '';
  @property({ type: String }) fetch = '';
  @property({ type: String }) group = '';

  eventTracking: Record<string, number> = {};

  store = comsStore;

  nodeTreeCompletelyRendered = false;
  private service = new Service(this); // binding service to <tef-coms4cs>
  private tracker = new Tracker();
  private intersectionObserver?: IntersectionObserver;
  private intersectionObserverInitialSkipDone: boolean = false;

  async connectedCallback(): Promise<void> {
    this.setTemplatingTheme(this.occlUri ? Theme.OCCL : Theme.TEFONE);

    try {
      comsStore.reset();
      // show more infos if debugmode is on
      if (localStorage.getItem('tef-coms-4cs-debug-mode')) {
        let timestampInit = 0;
        let timestampReady = 0;

        this.addEventListener('tef-coms4cs', (e) => {
          const { detail } = e as TefComs.Event;
          const channel = detail.level === 'error' ? 'error' : 'info';
          // eslint-disable-next-line no-console
          console[channel]('tef-coms: event', detail.name, e);
          if (detail.name === 'config/INIT') {
            timestampInit = (performance && performance.now) ? performance.now() : 0;
          }
          if (detail.name === 'load/READY') {
            timestampReady = (performance && performance.now) ? performance.now() : 0;
            // const el = <TefComs4csMicroFrontend>customElements.get('tef-coms4cs');
            const infoVersion = `version ${TefComs4csMicroFrontend.version}`;
            const infoReady = `Micro-frontend ready in ${Math.trunc(timestampReady - timestampInit)} ms, `;
            // eslint-disable-next-line no-console
            console.log(infoReady + infoVersion);
          }
        });
      }
      // issue 'config/INIT' immediately because it is used for performance measurements
      this.service.fireEvent<TefComs.EventDetails>({
        name: 'config/INIT',
        level: 'info',
        message: 'micro-frontend is initializing',

      });
      super.connectedCallback(); // start rendering immediately !!

      await this.store.init({
        apiUri: this.apiUri,
        agentToken: this.agentToken,
        gatewayToken: this.gatewayToken,
        lifecycle: this.lifecycle,
        mode: this.mode,
        traceid: this.traceid,
        occlUri: this.occlUri,
        styleContext: this.styleContext,
        consentwhitelist: this.consentwhitelist,
        channel: this.channel,
        usertype: this.usertype,
        vonr: this.vonr,
        references: this.references,
        showReferences: this.showReferences,
        showVersions: this.showVersions,
        isExistingRegister: this.isExistingRegister,
        isNewCustomer: this.isNewCustomer,
        spId: this.spId,
        lcId: this.lcId,
        registerId: this.registerId,
        fetch: this.fetch,
        group: this.group,
        generation: Generation.CLASSIC,
      });

      this.store.setGrantedValueForGroups();

      this.shadowRoot?.addEventListener(
        'occl.collapsible.open',
        (event) => {
          const { id: name } = <HTMLDivElement>event?.target;
          this.tracker.clickCollapsable(name);
          const consentGroup = this.store.findGroupByName(name);
          if (consentGroup) {
            runInAction(() => {
              consentGroup.collapsibleOpen = true;
            });
          }
        },
      );

      const { history } = comsStore;
      this.shadowRoot?.addEventListener(
        'occl.pager.open',
        (event) => history?.gotoPage((<CustomEvent>event).detail as number) as void,
      );

      // Observe this element getting visible/invisible in regards to the viewport when in "group" mode
      if (this.group) {
        this.installIntersectionObserver();
      }

      // eslint-disable-next-line sonarjs/no-ignored-exceptions
    } catch (error) {
      // ignore -- has error condition in store
    }
  }

  disconnectedCallback() {
    this.removeIntersectionObserver();
  }

  // We need to get notified if this instance of the MFE is visible to trigger a render() loop.
  // This is used for "Retail Activate" which renders multiple MFE instances on the same page.
  installIntersectionObserver() {
    if (typeof globalThis.IntersectionObserver === 'undefined') {
      return null;
    }

    this.intersectionObserver = new IntersectionObserver((entries) => {
      // Skip the initial call since the IntersectionObserver is called once for init
      // See https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserver/observe
      if (!this.intersectionObserverInitialSkipDone) {
        this.intersectionObserverInitialSkipDone = true;
        return;
      }

      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.updateRenderedConsentVersionIds();
        }
      });
    }, {
      root: null, // observes the viewport
      threshold: 0.1, // trigger when at least 10% is visible
    });

    // Start observing
    this.intersectionObserver.observe(this);
    return this.intersectionObserver;
  }

  removeIntersectionObserver() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  }

  updated() {
    // WPCSCM-1183: tefOne <one-popover> elements used here don't stop the event propagation by themselves.
    //              This causes a click on the pop over to also trigger the consent <one-checkbox> to be clicked resulting
    //              in a wrongly granted consent.
    if (!this.nodeTreeCompletelyRendered && this.occlUri === '') {
      const onePopovers$ = this.renderRoot.querySelectorAll('one-popover');

      // Assume all popovers are rendered once we have at least 1
      if (onePopovers$.length > 0) {
        this.nodeTreeCompletelyRendered = true;

        const handler = (event : Event) => event.stopPropagation();
        onePopovers$.forEach((el$) => {
          el$.addEventListener('click', handler);
        });
      }
    }
  }

  /**
   * returns filtered list of consentGroup for rendering.
   *
   * Either returns the complete list or - in case of property "group" usage - the requested consent groups
   */
  get displayConsentGroups() {
    // We might get called to early
    if (!this.store.consentGroups) {
      return [];
    }

    // No "groups" attribute
    if (!this.group || this.group === '') {
      return this.store.consentGroups;
    }

    const finalGroups: ConsentGroupStore[] = [];
    const groups: string[] = this.group.split(',').map((g) => g.toLowerCase().trim());

    // Put groups into final in the wanted order
    groups.forEach((groupName) => {
      const consentGroup = this.store.consentGroups?.find(
        (foundGroup) => foundGroup.name?.toLowerCase().trim() === groupName,
      );

      if (consentGroup) {
        // Add consent to list and also add the generation
        finalGroups.push(consentGroup);
      }
    });

    // No groups found -> empty
    if (finalGroups.length === 0 && !this.eventTracking['consents/EMPTY']) {
      this.service.fireEvent<TefComs.EventDetails>({
        name: 'consents/EMPTY',
        level: 'info',
        message: 'no consents to be rendered (groups)',
      });
      this.eventTracking['consents/EMPTY'] = 1;
      return [];
    }

    return finalGroups;
  }

  /**
  * Mark all consent version ids inside a consent group as rendered/processed.
  */
  addRenderedConsentGroup(consentGroup: ConsentGroupStore) {
    consentGroup.consents.forEach((consent) => {
      this.store.addRenderedConsentVersionId(consentGroup.name, consent.id);
    });
  }

  /**
  * Remove all consent version ids inside a consent group from the list of rendered/processed consents.
  */
  removeRenderedConsentGroup(consentGroup: ConsentGroupStore) {
    consentGroup.consents.forEach((consent) => {
      this.store.removeRenderedConsentVersionId(consent.id);
    });
  }

  static get version(): string {
    return _MFE_VERSION; // Filled/replaced by esbuild (see scripts/esbuild/*.ts)
  }

  async save(): Promise<void> {
    await this.store.save();
  }

  async undo() {
    this.store.undo();
    return Promise.resolve();
  }

  cancel() {
    this.store.cancel();
  }

  getConsents(): TefComs.ShortConsent[] {
    return this.store.getShortConsents();
  }

  getConsentStatus(): TefComs.ConsentAndCustomerAttributeSaveType[] {
    return this.store.getConsentStatus();
  }

  getReferenceLength(): number {
    return this.store.references?.length || 0;
  }

  updateRenderedConsentVersionIds() {
    // See https://developer.mozilla.org//en-US/docs/Web/API/Element/checkVisibility
    const isMfeVisible = typeof Element.prototype.checkVisibility === 'undefined' ? true : this.checkVisibility();

    if (this.displayConsentGroups && isMfeVisible) {
      this.displayConsentGroups.forEach((consentGroup) => {
        // For "hidden" groups that have NO grantedDefault -> remove them from the list of "seen" consent version ids
        // See page config for group "iban-hidden" in live-config
        if (consentGroup.displayMode === DisplayMode.HIDDEN && typeof consentGroup.grantedDefault === 'undefined') {
          this.removeRenderedConsentGroup(consentGroup);
        } else {
          this.addRenderedConsentGroup(consentGroup);
        }
      });
    }
  }

  render(): TemplateResult {
    // If fatal error condition (not recoverable), render nothing.
    // Render stylesheet link as early as possible (for performance reasons).
    // Prevent FOUC effect after loading the OCCL resources.
    if (this.store.fatal) {
      return html`<!-- fatal error condition -->`;
    }

    if (!this.displayConsentGroups) {
      return html`${nothing}`;
    }

    const isLoading = (!this.store.isDataLoaded && this.store.client?.processing)
    || (this.store.occl && !this.store.occl.ready);

    // Update the rendered version ids only once
    if (!isLoading) {
      this.updateRenderedConsentVersionIds();
    }

    /* eslint-disable @typescript-eslint/indent */
    return html`<one-stack name="tef-coms4cs">
      ${this.store.occl?.renderStylesheet()}
      ${isLoading
        ? nothing
        : this.getTemplateFn('mainTemplate')()
      }
      ${this.mode.includes('buttons')
       ? this.getTemplateFn('buttonsTemplate')() : nothing}
      </one-stack>`;
  }
}
